import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Alert } from '@/components/ui/Alert'
import { Badge } from '@/components/ui/Badge'
import { PostcodeLookup } from '@/components/ui/PostcodeLookup'
import { Address } from '@/services/postcode'
import { useCartStore } from '@/stores/cartStore'
import { useAuthStore } from '@/stores/authStore'
import { ordersService } from '@/services/orders'
import PaymentLogos from '@/components/PaymentLogos'
import { StripePayment } from '@/components/payment/StripePayment'
import { CreateOrderRequest, CreateOrderResponse, Product } from '@/types'
import api from '@/services/api'
import { getStoredReferralCode, clearReferralCode as clearReferralCookie } from '@/utils/referralTracking'
import { setAuthReturn, clearAuthReturn } from '@/utils/authReturn'
import CheckoutAccount from '@/components/CheckoutAccount'

interface CheckoutFormData {
  // Billing Address
  billing_first_name: string
  billing_last_name: string
  billing_email: string
  billing_phone: string
  billing_address_line1: string
  billing_address_line2: string
  billing_city: string
  billing_county: string
  billing_postcode: string
  // Shipping Address
  same_as_billing: boolean
  shipping_first_name: string
  shipping_last_name: string
  shipping_address_line1: string
  shipping_address_line2: string
  shipping_city: string
  shipping_county: string
  shipping_postcode: string
  // Shipping
  shipping_method: 'tracked_48' | 'tracked_24' | 'next_day'
  // Payment
  payment_method: 'card' | 'paypal'
  // Discount
  discount_code: string
  // Terms
  terms_accepted: boolean
}

// Networks that show the Physical SIM / eSIM selector — fetched from admin settings
// A product needs SIM format selection if it explicitly supports eSIM
// (physical SIM is always available unless supports_physical_sim is explicitly false)
function isSimFormatEligible(product: Product): boolean {
  if (product.product_type !== 'sim_only') return false
  if (!product.is_pay_and_go && !product.is_contract) return false
  return !!(product as any).supports_esim
}

export default function Checkout() {
  const navigate = useNavigate()
  const { items, clearCart, getTotal, setSimFormat } = useCartStore()
  const { user } = useAuthStore()
  // Sign up / sign in inside checkout (Sales Strategy, owner 2026-10-15) — switchable in
  // Admin → Sales Strategy → Settings; on unless switched off.
  const { data: salesCfg } = useQuery({
    queryKey: ['sales-config'], staleTime: 5 * 60 * 1000,
    queryFn: async () => (await api.get('/sales/config')).data as { checkout_inline_signup: boolean },
  })
  const total = getTotal()
  const [currentStep, setCurrentStep] = useState(1)
  const [simFormatError, setSimFormatError] = useState('')
  const [discountApplied, setDiscountApplied] = useState<{ type: string; value: number } | null>(null)
  const [spinWheelBlockedProducts, setSpinWheelBlockedProducts] = useState<string[] | null>(null)
  // Contract carts can be paid in full upfront (whole discounted term) instead of monthly DD.
  const [payUpfront, setPayUpfront] = useState(false)
  const [formData, setFormData] = useState<CheckoutFormData>({
    billing_first_name: user?.first_name || '',
    billing_last_name: user?.last_name || '',
    billing_email: user?.email || '',
    billing_phone: user?.mobile_number || '',
    billing_address_line1: user?.address_line1 || '',
    billing_address_line2: user?.address_line2 || '',
    billing_city: user?.city || '',
    billing_county: user?.county || '',
    billing_postcode: user?.postcode || '',
    same_as_billing: true,
    shipping_first_name: '',
    shipping_last_name: '',
    shipping_address_line1: '',
    shipping_address_line2: '',
    shipping_city: '',
    shipping_county: '',
    shipping_postcode: '',
    shipping_method: 'tracked_48',
    payment_method: 'card',
    discount_code: '',
    terms_accepted: false,
  })
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})
  const [usePostcodeLookup, setUsePostcodeLookup] = useState(true)
  const orderSuccessRef = useRef(false)
  // The order created for this checkout attempt. Re-submitting (e.g. switching PayPal↔card)
  // must REUSE this order and just (re)start payment — never create a second order.
  const createdOrderRef = useRef<{ orderId: number; total: number } | null>(null)

  // Payment step (Stripe card / Apple Pay / Google Pay). PayPal redirects away instead.
  const [payState, setPayState] = useState<{
    step: 'idle' | 'paying'
    clientSecret?: string
    paymentRef?: string
    orderId?: number
    total?: number
  }>({ step: 'idle' })
  const [payError, setPayError] = useState<string | null>(null)

  // Referral code state
  const [referralCode, setReferralCode] = useState('')
  const [referralValid, setReferralValid] = useState<{ valid: boolean; referrer_name: string | null } | null>(null)
  const [referralValidating, setReferralValidating] = useState(false)


  // Contract carts are set up as a plain monthly Direct Debit; bank details + the collection
  // date are captured later when the contract is sent for signing (not at checkout).
  const hasContractItems = items.some(item => item.product.is_contract)

  // Check if any items need SIM format selection
  const eligibleSimItems = items.filter(item => isSimFormatEligible(item.product))
  const hasEligibleSimItems = eligibleSimItems.length > 0

  // Auto-populate referral code from cookie/localStorage
  useEffect(() => {
    const stored = getStoredReferralCode()
    if (stored && !referralCode) {
      setReferralCode(stored)
      // Auto-validate
      handleValidateReferral(stored)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (items.length === 0 && !orderSuccessRef.current) {
      navigate('/shop')
    }
  }, [items, navigate])

  // Signed-in customer has arrived at checkout — consume the post-login "return to checkout"
  // flag so it can't linger and misdirect a later, unrelated login.
  useEffect(() => {
    if (user) {
      clearAuthReturn()
    }
  }, [user])

  // Fetch fresh user data when checkout loads to ensure we have current address
  useEffect(() => {
    const fetchFreshUserData = async () => {
      try {
        const response = await api.get('/auth/me')
        const freshUser = response.data
        if (freshUser) {
          // Always prioritize fresh data from API - don't use prev values that might be empty
          setFormData((prev) => ({
            ...prev,
            billing_first_name: freshUser.first_name || '',
            billing_last_name: freshUser.last_name || '',
            billing_email: freshUser.email || '',
            billing_phone: freshUser.mobile_number || '',
            billing_address_line1: freshUser.address_line1 || '',
            billing_address_line2: freshUser.address_line2 || '',
            billing_city: freshUser.city || '',
            billing_county: freshUser.county || '',
            billing_postcode: freshUser.postcode || '',
          }))
        }
      } catch (error) {
        console.log('Could not fetch fresh user data, using stored data')
      }
    }

    fetchFreshUserData()
  }, [])

  // Update billing address when user data becomes available (only if fields are empty)
  // This is a fallback - the fresh fetch useEffect should handle most cases
  useEffect(() => {
    if (user) {
      setFormData((prev) => {
        // Only update if billing fields are completely empty (not yet populated)
        const needsUpdate = !prev.billing_first_name && !prev.billing_email && !prev.billing_address_line1
        if (!needsUpdate) return prev

        return {
          ...prev,
          billing_first_name: user.first_name || '',
          billing_last_name: user.last_name || '',
          billing_email: user.email || '',
          billing_phone: user.mobile_number || '',
          billing_address_line1: user.address_line1 || '',
          billing_address_line2: user.address_line2 || '',
          billing_city: user.city || '',
          billing_county: user.county || '',
          billing_postcode: user.postcode || '',
        }
      })
    }
  }, [user])

  // (Re)start payment for an already-created order. Used both right after the order is
  // created and on any re-submit, so switching PayPal↔card or retrying never makes a 2nd order.
  const startPaymentForOrder = async (orderId: number, totalAmount: number) => {
    setPayError(null)
    try {
      if (formData.payment_method === 'paypal') {
        // PayPal — hand off to PayPal's hosted approval, then it returns to confirmation.
        const pay = await ordersService.createPayment(orderId, 'paypal', window.location.origin)
        if (pay.approval_url) {
          window.location.href = pay.approval_url
          return
        }
        throw new Error('Could not start PayPal checkout.')
      }
      // Card — Stripe PaymentElement (also presents Apple Pay / Google Pay).
      const pay = await ordersService.createPayment(orderId, 'stripe')
      if (!pay.client_secret) throw new Error('Could not start card payment.')
      setPayState({
        step: 'paying',
        clientSecret: pay.client_secret,
        paymentRef: pay.payment_reference,
        orderId,
        total: totalAmount,
      })
    } catch (e: any) {
      setPayError(e?.response?.data?.detail || e?.message || 'Could not start payment. Please try again.')
    }
  }

  const createOrderMutation = useMutation({
    mutationFn: ordersService.createOrder,
    onSuccess: async (data: CreateOrderResponse) => {
      // Keep the cart until payment actually succeeds; just block the empty-cart redirect.
      orderSuccessRef.current = true
      const totalAmount = Number(data.total_amount) || total
      createdOrderRef.current = { orderId: data.order_id, total: totalAmount }
      await startPaymentForOrder(data.order_id, totalAmount)
    },
    onError: (e: any) => {
      setPayError(e?.response?.data?.detail || 'Could not place the order. Please try again.')
    },
  })

  // After the Stripe PaymentElement confirms the payment, finalise and go to confirmation.
  const handlePaymentSuccess = async () => {
    try {
      if (payState.paymentRef) await ordersService.confirmPayment(payState.paymentRef)
    } catch (_) {
      // Payment already succeeded on Stripe's side; the webhook will reconcile if confirm fails.
    }
    clearCart()
    clearReferralCookie()
    navigate(`/order-confirmation/${payState.orderId}`)
  }

  const validateDiscountMutation = useMutation({
    mutationFn: ({ code, productIds }: { code: string; productIds: number[] }) =>
      ordersService.validateDiscount(code, productIds),
    onSuccess: (data) => {
      if (data.valid) {
        setDiscountApplied({ type: data.discount_type, value: data.discount_value })
        setSpinWheelBlockedProducts(null)
      }
    },
    onError: (err: any) => {
      const detail: string = err?.response?.data?.detail || ''
      if (detail.startsWith('SPIN_WHEEL_INELIGIBLE:')) {
        const names = detail.replace('SPIN_WHEEL_INELIGIBLE:', '').split(',').map((s: string) => s.trim())
        setSpinWheelBlockedProducts(names)
      }
    },
  })

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount)
  }

  // SIM-only carts are charged the cheaper delivery rate; any non-SIM item bumps
  // the whole order to the standard (non-SIM) rate.
  const isSimOnly = items.length > 0 && items.every(
    (i) => i.product.product_type === 'sim_only' || i.product.product_type === 'free_sim'
  )

  const { data: deliveryPrices } = useQuery({
    queryKey: ['delivery-prices'],
    queryFn: async () => (await api.get('/orders/delivery-prices')).data as {
      sim_only: { tracked_24: number; next_day: number }
      non_sim: { tracked_24: number; next_day: number }
    },
    staleTime: 5 * 60 * 1000,
  })

  // Authoritative "pay the whole term upfront" quote for the contract items in the cart.
  // Single source of truth (same endpoint drives the charged amount) — full term total,
  // admin-set cash discount %, and the discounted payable.
  const { data: upfrontQuote } = useQuery({
    queryKey: ['upfront-quote', items.map(i => `${i.product.id}x${i.quantity}`).join(',')],
    queryFn: async () => (await api.post('/orders/upfront-quote',
      items.map(i => ({ product_id: i.product.id, quantity: i.quantity })))).data as {
        enabled: boolean; discount_percent: number; monthly_total: number
        full_term_total: number; payable: number; savings: number
        deposit_total?: number; then_direct_debit?: boolean; dd_after_monthly?: number
        items?: { contract_months?: number }[]
      },
    enabled: hasContractItems && items.length > 0,
  })
  const upfrontAvailable = !!upfrontQuote?.enabled
  // Fixed refundable holding deposit on broadband plans — charged on day one in BOTH modes
  // (the backend always adds it), never discounted. Shown so the total matches the charge.
  const depositTotal = upfrontQuote?.deposit_total || 0

  const deliveryFor = (method: 'tracked_24' | 'next_day') => {
    const fallback = isSimOnly
      ? { tracked_24: 3.95, next_day: 11.25 }
      : { tracked_24: 5.85, next_day: 17.10 }
    const set = deliveryPrices ? (isSimOnly ? deliveryPrices.sim_only : deliveryPrices.non_sim) : fallback
    return set[method] ?? fallback[method]
  }

  const getShippingCost = () => {
    if (formData.shipping_method === 'next_day') return deliveryFor('next_day')
    if (formData.shipping_method === 'tracked_24') return deliveryFor('tracked_24')
    return 0 // tracked_48 — always free
  }

  const getShippingLabel = () => {
    switch (formData.shipping_method) {
      case 'tracked_24':
        return 'Tracked 24'
      case 'next_day':
        return 'Special Next Day Delivery'
      case 'tracked_48':
      default:
        return 'Tracked 48'
    }
  }

  const calculateDiscount = () => {
    if (!discountApplied) return 0
    if (discountApplied.type === 'percentage') {
      return (total * discountApplied.value) / 100
    }
    return discountApplied.value
  }

  // When paying the whole term upfront, the order summary reflects the discounted upfront
  // payable instead of the monthly subtotal. The promo discount code only applies to the
  // monthly flow (the upfront cash discount is already baked into `payable`).
  const usingUpfront = payUpfront && upfrontAvailable && !!upfrontQuote
  const effectiveSubtotal = () => {
    if (!usingUpfront || !upfrontQuote) return total
    // Mixed cart: contract items charged at the discounted upfront price, any non-contract
    // items (Pay&Go / one-off) at their normal price. `monthly_total` is the contract portion
    // of `total`, so the remainder is the non-contract spend.
    const nonContract = Math.max(0, total - upfrontQuote.monthly_total)
    return upfrontQuote.payable + nonContract
  }
  const effectiveDiscount = () => (usingUpfront ? 0 : calculateDiscount())
  const effectiveVAT = () => {
    const afterDiscount = effectiveSubtotal() - effectiveDiscount()
    return afterDiscount - afterDiscount / 1.2
  }
  const effectiveTotal = () => effectiveSubtotal() - effectiveDiscount() + depositTotal + getShippingCost()

  const handleValidateReferral = async (code?: string) => {
    const codeToValidate = code || referralCode
    if (!codeToValidate) return
    setReferralValidating(true)
    try {
      const response = await api.post('/referrals/validate', { code: codeToValidate })
      setReferralValid(response.data)
    } catch {
      setReferralValid({ valid: false, referrer_name: null })
    } finally {
      setReferralValidating(false)
    }
  }

  const handleApplyDiscount = () => {
    if (formData.discount_code) {
      validateDiscountMutation.mutate({
        code: formData.discount_code,
        productIds: items.map((item) => item.product.id),
      })
    }
  }

  const validateStep1 = (): boolean => {
    const errors: Record<string, string> = {}

    // Billing address validation
    if (!formData.billing_first_name.trim()) {
      errors.billing_first_name = 'First name is required'
    }
    if (!formData.billing_last_name.trim()) {
      errors.billing_last_name = 'Last name is required'
    }
    if (!formData.billing_email.trim()) {
      errors.billing_email = 'Email is required'
    } else if (!/\S+@\S+\.\S+/.test(formData.billing_email)) {
      errors.billing_email = 'Please enter a valid email address'
    }
    if (!formData.billing_phone.trim()) {
      errors.billing_phone = 'Phone number is required'
    }
    if (!formData.billing_address_line1.trim()) {
      errors.billing_address_line1 = 'Address line 1 is required'
    }
    if (!formData.billing_city.trim()) {
      errors.billing_city = 'City is required'
    }
    if (!formData.billing_postcode.trim()) {
      errors.billing_postcode = 'Postcode is required'
    }

    // Shipping address validation (if different from billing)
    if (!formData.same_as_billing) {
      if (!formData.shipping_first_name.trim()) {
        errors.shipping_first_name = 'Shipping first name is required'
      }
      if (!formData.shipping_last_name.trim()) {
        errors.shipping_last_name = 'Shipping last name is required'
      }
      if (!formData.shipping_address_line1.trim()) {
        errors.shipping_address_line1 = 'Shipping address line 1 is required'
      }
      if (!formData.shipping_city.trim()) {
        errors.shipping_city = 'Shipping city is required'
      }
      if (!formData.shipping_postcode.trim()) {
        errors.shipping_postcode = 'Shipping postcode is required'
      }
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleContinueToStep2 = () => {
    if (validateStep1()) {
      // Go to SIM format step if there are eligible items, otherwise skip to shipping
      setCurrentStep(hasEligibleSimItems ? 2 : getShippingStepNumber())
    }
  }

  const handleContinueFromSimFormat = () => {
    // Validate all eligible items have a format selected (default is physical so this always passes,
    // but we defensively ensure the state is set)
    const missing = eligibleSimItems.filter(item => !item.sim_format)
    if (missing.length > 0) {
      // Auto-default to physical for any unset items
      missing.forEach(item => setSimFormat(item.product.id, 'physical'))
    }
    setSimFormatError('')
    setCurrentStep(getShippingStepNumber())
  }

  const handleShippingAddressSelect = (address: Address) => {
    setFormData({
      ...formData,
      shipping_address_line1: address.line_1,
      shipping_address_line2: address.line_2 || '',
      shipping_city: address.post_town,
      shipping_county: address.county || '',
      shipping_postcode: address.postcode,
    })
    // Clear any validation errors for shipping address fields
    setValidationErrors((prev) => {
      const newErrors = { ...prev }
      delete newErrors.shipping_address_line1
      delete newErrors.shipping_city
      delete newErrors.shipping_postcode
      return newErrors
    })
  }

  const handleSubmitOrder = () => {
    if (createOrderMutation.isPending) return
    // An order already exists for this checkout? Reuse it and just (re)start payment with the
    // currently-selected method — never create a duplicate order (e.g. PayPal then card).
    if (createdOrderRef.current) {
      startPaymentForOrder(createdOrderRef.current.orderId, createdOrderRef.current.total)
      return
    }

    // Build shipping address in the format expected by backend
    const shippingAddress = formData.same_as_billing
      ? {
          address_line1: formData.billing_address_line1,
          address_line2: formData.billing_address_line2 || undefined,
          city: formData.billing_city,
          county: formData.billing_county || undefined,
          postcode: formData.billing_postcode,
          country: 'United Kingdom',
        }
      : {
          address_line1: formData.shipping_address_line1,
          address_line2: formData.shipping_address_line2 || undefined,
          city: formData.shipping_city,
          county: formData.shipping_county || undefined,
          postcode: formData.shipping_postcode,
          country: 'United Kingdom',
        }

    const orderData: CreateOrderRequest = {
      items: items.map((item) => ({
        product_id: item.product.id,
        sku: item.product.sku || `PROD-${item.product.id}`,
        quantity: item.quantity,
        ...(isSimFormatEligible(item.product) && {
          sim_format: item.sim_format || 'physical',
        }),
      })),
      shipping_address: shippingAddress,
      delivery_method: formData.shipping_method,
      discount_code: discountApplied ? formData.discount_code : undefined,
      referral_code: referralValid?.valid ? referralCode : undefined,
      // Contract carts are a plain monthly Direct Debit; bank details + collection date are
      // captured later at contract-send, NOT at checkout. Unless the customer chose to pay the
      // whole discounted term upfront — then it's paid in full (no debit order). The backend
      // recomputes the discounted total authoritatively from pay_in_full.
      ...(hasContractItems && {
        recurring_payment_method: 'debit_order',
        pay_in_full: payUpfront && upfrontAvailable,
      }),
    }

    createOrderMutation.mutate(orderData)
  }

  // Get shipping address for display
  const getShippingAddressDisplay = () => {
    if (formData.same_as_billing) {
      return {
        name: `${formData.billing_first_name} ${formData.billing_last_name}`,
        line1: formData.billing_address_line1,
        line2: formData.billing_address_line2,
        city: formData.billing_city,
        county: formData.billing_county,
        postcode: formData.billing_postcode,
      }
    }
    return {
      name: `${formData.shipping_first_name} ${formData.shipping_last_name}`,
      line1: formData.shipping_address_line1,
      line2: formData.shipping_address_line2,
      city: formData.shipping_city,
      county: formData.shipping_county,
      postcode: formData.shipping_postcode,
    }
  }

  // Determine total steps
  // Flow: Billing -> [SIM Format] -> Shipping -> Review
  const calculateTotalSteps = () => {
    let steps = 3 // Base: Billing, Shipping, Review
    if (hasEligibleSimItems) steps += 1
    return steps
  }
  const totalSteps = calculateTotalSteps()

  // SIM format step number (step 2 when present, else undefined)
  const getSimFormatStepNumber = () => hasEligibleSimItems ? 2 : null

  // Shipping step number shifts by 1 if SIM format step is inserted
  const getShippingStepNumber = () => hasEligibleSimItems ? 3 : 2

  // Review is always the final step
  const getReviewStepNumber = () => totalSteps

  // Map the current step to what should be displayed
  const getStepLabel = (step: number) => {
    const labels: string[] = ['Billing']
    if (hasEligibleSimItems) labels.push('SIM Type')
    labels.push('Shipping')
    labels.push('Review')
    return labels[step - 1] || ''
  }

  const networkLabel = (n: string) => {
    switch (n) {
      case 'three': return 'Three'
      case 'vodafone': return 'Vodafone'
      case 'o2': return 'O2'
      case 'ee': return 'EE'
      default: return n.charAt(0).toUpperCase() + n.slice(1)
    }
  }

  const renderSimFormatStep = () => (
    <div className="space-y-6">
      <h2 className="text-lg sm:text-xl font-semibold">Choose SIM Type</h2>

      <Alert variant="info">
        Please choose whether you would like a <strong>Physical SIM</strong> card or an <strong>eSIM</strong> (digital SIM) for each of your SIM products below.
      </Alert>

      <div className="space-y-5">
        {eligibleSimItems.map((item) => {
          const selected = item.sim_format || 'physical'
          const simTypeLabel = item.product.sim_type === 'data_only' ? 'Data Only' : 'Calls, Texts & Data'
          // Only a real contract has a term; anything else is a one-off purchase (owner, 2026-10-02).
          const planLabel = item.product.is_pay_and_go
            ? 'Pay & Go'
            : item.product.is_contract
              ? (item.product.is_rolling_contract ? "Rolling monthly contract (30 days' notice)" : `${item.product.contract_months || 12}-month Contract`)
              : 'One-off purchase'

          return (
            <Card key={item.product.id} className="p-4 sm:p-5">
              <div className="flex items-start gap-3 mb-4">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 truncate">{item.product.name}</p>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {networkLabel(item.product.network_type || '')} · {simTypeLabel} · {planLabel}
                  </p>
                </div>
                {item.quantity > 1 && (
                  <Badge variant="default">×{item.quantity}</Badge>
                )}
              </div>

              {(() => {
                const supportsPhysical = (item.product as any).supports_physical_sim !== false
                const supportsEsim = !!(item.product as any).supports_esim
                const colCount = (supportsPhysical && supportsEsim) ? 2 : 1
                return (
                  <div className={`grid grid-cols-${colCount} gap-3`}>
                    {/* Physical SIM option */}
                    {supportsPhysical && (
                      <label
                        className={`flex flex-col items-center gap-2 p-4 border-2 rounded-xl cursor-pointer transition-all select-none ${
                          selected === 'physical'
                            ? 'border-silux-primary bg-blue-50 shadow-sm'
                            : 'border-gray-200 hover:border-gray-300 bg-white'
                        }`}
                        onClick={() => setSimFormat(item.product.id, 'physical')}
                      >
                        <input
                          type="radio"
                          name={`sim_format_${item.product.id}`}
                          value="physical"
                          checked={selected === 'physical'}
                          onChange={() => setSimFormat(item.product.id, 'physical')}
                          className="sr-only"
                        />
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center text-2xl ${
                          selected === 'physical' ? 'bg-blue-100' : 'bg-gray-100'
                        }`}>
                          💳
                        </div>
                        <div className="text-center">
                          <p className={`font-semibold text-sm ${selected === 'physical' ? 'text-silux-primary' : 'text-gray-700'}`}>
                            Physical SIM
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5 leading-tight">
                            Standard plastic SIM card delivered by post
                          </p>
                        </div>
                        {selected === 'physical' && (
                          <span className="text-xs font-medium text-silux-primary bg-blue-100 px-2 py-0.5 rounded-full">
                            Selected
                          </span>
                        )}
                      </label>
                    )}

                    {/* eSIM option */}
                    {supportsEsim && (
                      <label
                        className={`flex flex-col items-center gap-2 p-4 border-2 rounded-xl cursor-pointer transition-all select-none ${
                          selected === 'esim'
                            ? 'border-silux-primary bg-blue-50 shadow-sm'
                            : 'border-gray-200 hover:border-gray-300 bg-white'
                        }`}
                        onClick={() => setSimFormat(item.product.id, 'esim')}
                      >
                        <input
                          type="radio"
                          name={`sim_format_${item.product.id}`}
                          value="esim"
                          checked={selected === 'esim'}
                          onChange={() => setSimFormat(item.product.id, 'esim')}
                          className="sr-only"
                        />
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center text-2xl ${
                          selected === 'esim' ? 'bg-blue-100' : 'bg-gray-100'
                        }`}>
                          📱
                        </div>
                        <div className="text-center">
                          <p className={`font-semibold text-sm ${selected === 'esim' ? 'text-silux-primary' : 'text-gray-700'}`}>
                            eSIM
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5 leading-tight">
                            Digital SIM — no physical card needed
                          </p>
                        </div>
                        {selected === 'esim' && (
                          <span className="text-xs font-medium text-silux-primary bg-blue-100 px-2 py-0.5 rounded-full">
                            Selected
                          </span>
                        )}
                      </label>
                    )}
                  </div>
                )
              })()}

              {selected === 'esim' && (
                <p className="text-xs text-gray-500 mt-3 flex items-start gap-1.5">
                  <span className="mt-0.5">ℹ️</span>
                  eSIM requires a compatible device. Your QR code activation details will be sent by email after your order is processed.
                </p>
              )}
            </Card>
          )
        })}
      </div>

      {simFormatError && (
        <Alert variant="error">{simFormatError}</Alert>
      )}

      <div className="flex justify-between pt-2">
        <Button variant="outline" onClick={() => setCurrentStep(1)}>
          Back
        </Button>
        <Button variant="primary" onClick={handleContinueFromSimFormat}>
          Continue to Shipping
        </Button>
      </div>
    </div>
  )

  const renderStep1 = () => (
    <div className="space-y-6">
      <h2 className="text-lg sm:text-xl font-semibold">Billing Information</h2>

      {Object.keys(validationErrors).length > 0 && (
        <Alert variant="error">
          Please fill in all required fields before continuing.
        </Alert>
      )}

      <div className="grid grid-cols-1 xs:grid-cols-2 gap-4">
        <Input
          label="First Name"
          value={formData.billing_first_name}
          onChange={(e) => setFormData({ ...formData, billing_first_name: e.target.value })}
          error={validationErrors.billing_first_name}
          required
        />
        <Input
          label="Last Name"
          value={formData.billing_last_name}
          onChange={(e) => setFormData({ ...formData, billing_last_name: e.target.value })}
          error={validationErrors.billing_last_name}
          required
        />
      </div>
      <Input
        label="Email"
        type="email"
        value={formData.billing_email}
        onChange={(e) => setFormData({ ...formData, billing_email: e.target.value })}
        error={validationErrors.billing_email}
        required
      />
      <Input
        label="Phone Number"
        type="tel"
        value={formData.billing_phone}
        onChange={(e) => setFormData({ ...formData, billing_phone: e.target.value })}
        error={validationErrors.billing_phone}
        required
      />
      <Input
        label="Address Line 1"
        value={formData.billing_address_line1}
        onChange={(e) => setFormData({ ...formData, billing_address_line1: e.target.value })}
        error={validationErrors.billing_address_line1}
        required
      />
      <Input
        label="Address Line 2"
        value={formData.billing_address_line2}
        onChange={(e) => setFormData({ ...formData, billing_address_line2: e.target.value })}
      />
      <div className="grid grid-cols-1 xs:grid-cols-2 gap-4">
        <Input
          label="City"
          value={formData.billing_city}
          onChange={(e) => setFormData({ ...formData, billing_city: e.target.value })}
          error={validationErrors.billing_city}
          required
        />
        <Input
          label="County"
          value={formData.billing_county}
          onChange={(e) => setFormData({ ...formData, billing_county: e.target.value })}
        />
      </div>
      <Input
        label="Postcode"
        value={formData.billing_postcode}
        onChange={(e) => setFormData({ ...formData, billing_postcode: e.target.value })}
        error={validationErrors.billing_postcode}
        required
      />

      <div className="pt-4">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={formData.same_as_billing}
            onChange={(e) => setFormData({ ...formData, same_as_billing: e.target.checked })}
          />
          <span>Shipping address same as billing</span>
        </label>
      </div>

      {!formData.same_as_billing && (
        <>
          <h3 className="text-lg font-semibold pt-4">Shipping Address</h3>
          <div className="grid grid-cols-1 xs:grid-cols-2 gap-4">
            <Input
              label="First Name"
              value={formData.shipping_first_name}
              onChange={(e) => setFormData({ ...formData, shipping_first_name: e.target.value })}
              error={validationErrors.shipping_first_name}
              required
            />
            <Input
              label="Last Name"
              value={formData.shipping_last_name}
              onChange={(e) => setFormData({ ...formData, shipping_last_name: e.target.value })}
              error={validationErrors.shipping_last_name}
              required
            />
          </div>

          {usePostcodeLookup ? (
            <>
              <PostcodeLookup
                onAddressSelect={handleShippingAddressSelect}
                label="Find Shipping Address by Postcode"
                error={validationErrors.shipping_postcode}
              />
              {formData.shipping_address_line1 && (
                <div className="p-3 bg-gray-50 rounded-md">
                  <p className="text-sm font-medium text-gray-700">Selected Address:</p>
                  <p className="text-sm text-gray-600">{formData.shipping_address_line1}</p>
                  {formData.shipping_address_line2 && (
                    <p className="text-sm text-gray-600">{formData.shipping_address_line2}</p>
                  )}
                  <p className="text-sm text-gray-600">
                    {formData.shipping_city}, {formData.shipping_county} {formData.shipping_postcode}
                  </p>
                </div>
              )}
              <button
                type="button"
                onClick={() => setUsePostcodeLookup(false)}
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                Enter address manually instead
              </button>
            </>
          ) : (
            <>
              <Input
                label="Address Line 1"
                value={formData.shipping_address_line1}
                onChange={(e) => setFormData({ ...formData, shipping_address_line1: e.target.value })}
                error={validationErrors.shipping_address_line1}
                required
              />
              <Input
                label="Address Line 2"
                value={formData.shipping_address_line2}
                onChange={(e) => setFormData({ ...formData, shipping_address_line2: e.target.value })}
              />
              <div className="grid grid-cols-1 xs:grid-cols-2 gap-4">
                <Input
                  label="City"
                  value={formData.shipping_city}
                  onChange={(e) => setFormData({ ...formData, shipping_city: e.target.value })}
                  error={validationErrors.shipping_city}
                  required
                />
                <Input
                  label="County"
                  value={formData.shipping_county}
                  onChange={(e) => setFormData({ ...formData, shipping_county: e.target.value })}
                />
              </div>
              <Input
                label="Postcode"
                value={formData.shipping_postcode}
                onChange={(e) => setFormData({ ...formData, shipping_postcode: e.target.value })}
                error={validationErrors.shipping_postcode}
                required
              />
              <button
                type="button"
                onClick={() => setUsePostcodeLookup(true)}
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                Use postcode lookup instead
              </button>
            </>
          )}
        </>
      )}

      <div className="flex justify-end">
        <Button variant="primary" onClick={handleContinueToStep2}>
          Continue to Shipping & Payment
        </Button>
      </div>
    </div>
  )

  const renderStep2 = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold">Shipping Method</h2>
      <Select
        label="Select Shipping Method"
        value={formData.shipping_method}
        onChange={(e) => setFormData({ ...formData, shipping_method: e.target.value as 'tracked_48' | 'tracked_24' | 'next_day' })}
        options={[
          { value: 'tracked_48', label: `Tracked 48 (2-3 Business Days) - FREE` },
          { value: 'tracked_24', label: `Tracked 24 (Next Business Day) - ${formatCurrency(deliveryFor('tracked_24'))}` },
          { value: 'next_day', label: `Special Next Day Delivery (Guaranteed) - ${formatCurrency(deliveryFor('next_day'))}` },
        ]}
      />

      {formData.shipping_method === 'tracked_48' && (
        <Alert variant="info">
          Free delivery within 2-3 business days with full tracking.
        </Alert>
      )}

      {formData.shipping_method === 'tracked_24' && (
        <Alert variant="info">
          Express delivery by the next business day with full tracking.
        </Alert>
      )}

      {formData.shipping_method === 'next_day' && (
        <Alert variant="info">
          Guaranteed next day delivery by 1pm with signature required.
        </Alert>
      )}

      <h2 className="text-xl font-semibold pt-4">Payment Method</h2>
      {/* Always-visible tiles (was a dropdown — customers missed PayPal because it stayed
          hidden until you opened the Select, and Card was the default). */}
      <div className="grid gap-3 sm:grid-cols-2">
        <label className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${formData.payment_method === 'card' ? 'border-silux-primary bg-silux-primary/5' : 'border-gray-200 hover:border-gray-300'}`}>
          <input
            type="radio"
            name="paymentMethod"
            className="mt-1"
            checked={formData.payment_method === 'card'}
            onChange={() => setFormData({ ...formData, payment_method: 'card' })}
          />
          <span>
            <span className="block font-medium">Credit / Debit Card</span>
            <span className="block text-sm text-gray-500">Visa, Mastercard, Amex — plus Apple&nbsp;Pay &amp; Google&nbsp;Pay</span>
          </span>
        </label>
        <label className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${formData.payment_method === 'paypal' ? 'border-silux-primary bg-silux-primary/5' : 'border-gray-200 hover:border-gray-300'}`}>
          <input
            type="radio"
            name="paymentMethod"
            className="mt-1"
            checked={formData.payment_method === 'paypal'}
            onChange={() => setFormData({ ...formData, payment_method: 'paypal' })}
          />
          <span>
            <span className="block font-medium">PayPal</span>
            <span className="block text-sm text-gray-500">Pay with your PayPal account</span>
          </span>
        </label>
      </div>

      <PaymentLogos />

      {formData.payment_method === 'card' && (
        <Alert variant="info">
          You will be redirected to our secure payment gateway to complete your card payment.
          Apple&nbsp;Pay and Google&nbsp;Pay are also available there.
        </Alert>
      )}

      {formData.payment_method === 'paypal' && (
        <Alert variant="info">
          You will be redirected to PayPal to complete your payment.
        </Alert>
      )}

      <div className="pt-4">
        <label className="block text-sm font-medium mb-2">Discount Code</label>
        <div className="flex gap-2">
          <Input
            value={formData.discount_code}
            onChange={(e) => setFormData({ ...formData, discount_code: e.target.value })}
            placeholder="Enter discount code"
          />
          <Button
            variant="outline"
            onClick={handleApplyDiscount}
            disabled={validateDiscountMutation.isPending}
          >
            Apply
          </Button>
        </div>
        {discountApplied && (
          <p className="text-green-600 text-sm mt-2">
            Discount applied: {discountApplied.type === 'percentage'
              ? `${discountApplied.value}% off`
              : formatCurrency(discountApplied.value)}
          </p>
        )}
      </div>

      <div className="pt-4">
        <label className="block text-sm font-medium mb-2">Referral Code</label>
        <div className="flex gap-2">
          <Input
            value={referralCode}
            onChange={(e) => {
              setReferralCode(e.target.value)
              setReferralValid(null)
            }}
            placeholder="Enter referral code"
          />
          <Button
            variant="outline"
            onClick={() => handleValidateReferral()}
            disabled={referralValidating || !referralCode}
          >
            {referralValidating ? 'Checking...' : 'Apply'}
          </Button>
        </div>
        {referralValid?.valid && (
          <p className="text-green-600 text-sm mt-2">
            Referred by: {referralValid.referrer_name}
          </p>
        )}
        {referralValid && !referralValid.valid && (
          <p className="text-red-600 text-sm mt-2">Invalid referral code</p>
        )}
      </div>

      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={() => setCurrentStep(hasEligibleSimItems ? 2 : 1)}>
          Back
        </Button>
        <Button variant="primary" onClick={() => setCurrentStep(getReviewStepNumber())}>
          Review Order
        </Button>
      </div>
    </div>
  )

  const renderStep3 = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold">Review Your Order</h2>

      <Card className="p-4">
        <h3 className="font-semibold mb-3">Order Items</h3>
        {items.map((item) => {
          const eligible = isSimFormatEligible(item.product)
          const fmt = eligible ? (item.sim_format || 'physical') : null
          return (
            <div key={item.product.id} className="flex justify-between py-2 border-b last:border-0">
              <div>
                <p className="font-medium">{item.product.name}</p>
                <p className="text-sm text-gray-500">Qty: {item.quantity}</p>
                {fmt && (
                  <p className="text-xs text-silux-primary font-medium mt-0.5">
                    {fmt === 'esim' ? '📱 eSIM' : '💳 Physical SIM'}
                  </p>
                )}
              </div>
              <p className="font-medium">{formatCurrency((item.product.monthly_price || item.product.one_time_price || 0) * item.quantity)}</p>
            </div>
          )
        })}
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="p-4">
          <h3 className="font-semibold mb-3">Billing Address</h3>
          <p>{formData.billing_first_name} {formData.billing_last_name}</p>
          <p>{formData.billing_address_line1}</p>
          {formData.billing_address_line2 && <p>{formData.billing_address_line2}</p>}
          <p>{formData.billing_city}{formData.billing_county ? `, ${formData.billing_county}` : ''}</p>
          <p>{formData.billing_postcode}</p>
          <p className="mt-2 text-gray-600">{formData.billing_email}</p>
          <p className="text-gray-600">{formData.billing_phone}</p>
        </Card>

        <Card className="p-4">
          <h3 className="font-semibold mb-3">Shipping Address</h3>
          {(() => {
            const shipping = getShippingAddressDisplay()
            return (
              <>
                <p>{shipping.name}</p>
                <p>{shipping.line1}</p>
                {shipping.line2 && <p>{shipping.line2}</p>}
                <p>{shipping.city}{shipping.county ? `, ${shipping.county}` : ''}</p>
                <p>{shipping.postcode}</p>
                {formData.same_as_billing && (
                  <p className="mt-2 text-sm text-gray-500 italic">Same as billing address</p>
                )}
              </>
            )
          })()}
        </Card>
      </div>

      <Card className="p-4">
        <h3 className="font-semibold mb-3">Shipping Method</h3>
        <p>Royal Mail, {getShippingLabel()}</p>
        <p className="text-sm text-gray-500">
          {getShippingCost() === 0 ? 'Free' : formatCurrency(getShippingCost())}
        </p>
      </Card>

      <Card className="p-4">
        <h3 className="font-semibold mb-3">Payment Method</h3>
        <p className="capitalize">{formData.payment_method.replace('_', ' ')}</p>
      </Card>

      {upfrontAvailable && upfrontQuote && (
        <Card className="p-4 border-2 border-silux-primary/20">
          <h3 className="font-semibold mb-3">How would you like to pay?</h3>
          <div className="space-y-2">
            <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer ${!payUpfront ? 'border-silux-primary bg-silux-primary/5' : 'border-gray-200'}`}>
              <input type="radio" name="payMode" className="mt-1" checked={!payUpfront} onChange={() => setPayUpfront(false)} />
              <div className="flex-1">
                <p className="font-medium">Pay monthly by Direct Debit</p>
                <p className="text-sm text-gray-500">{formatCurrency(upfrontQuote.monthly_total)} / month for the contract term</p>
              </div>
            </label>
            <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer ${payUpfront ? 'border-silux-primary bg-silux-primary/5' : 'border-gray-200'}`}>
              <input type="radio" name="payMode" className="mt-1" checked={payUpfront} onChange={() => setPayUpfront(true)} />
              <div className="flex-1">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-medium">Pay full term upfront</p>
                  <p className="font-bold text-silux-primary">{formatCurrency(upfrontQuote.payable)}</p>
                </div>
                <p className="text-sm text-gray-500">
                  Full term {formatCurrency(upfrontQuote.full_term_total)}
                  {upfrontQuote.discount_percent > 0 && (
                    <span className="text-green-600 font-medium"> · save {upfrontQuote.discount_percent}% ({formatCurrency(upfrontQuote.savings)})</span>
                  )}
                </p>
                {depositTotal > 0 && (
                  <p className="text-xs text-gray-500 mt-0.5">+ {formatCurrency(depositTotal)} refundable holding deposit (fixed)</p>
                )}
                {upfrontQuote.then_direct_debit ? (
                  <p className="text-xs text-gray-500 mt-0.5">
                    Covers the first {upfrontQuote.items?.[0]?.contract_months || ''} months. After that your Direct Debit
                    of {formatCurrency(upfrontQuote.dd_after_monthly || 0)}/month starts — you set it up when you sign your contract.
                  </p>
                ) : upfrontQuote.discount_percent > 0 && (
                  <p className="text-xs text-gray-400 mt-0.5">No Direct Debit — one payment covers the whole contract.</p>
                )}
              </div>
            </label>
          </div>
        </Card>
      )}

      <div className="bg-gray-50 p-4 rounded">
        <div className="space-y-2">
          <div className="flex justify-between">
            <span>{usingUpfront ? 'Upfront total (full term)' : 'Subtotal'}</span>
            <span>{formatCurrency(effectiveSubtotal())}</span>
          </div>
          {usingUpfront && upfrontQuote && upfrontQuote.discount_percent > 0 && (
            <div className="flex justify-between text-green-600">
              <span>Upfront discount ({upfrontQuote.discount_percent}%)</span>
              <span>-{formatCurrency(upfrontQuote.savings)}</span>
            </div>
          )}
          {!usingUpfront && discountApplied && (
            <div className="flex justify-between text-green-600">
              <span>Discount</span>
              <span>-{formatCurrency(calculateDiscount())}</span>
            </div>
          )}
          {depositTotal > 0 && (
            <div className="flex justify-between">
              <span>Refundable holding deposit</span>
              <span>{formatCurrency(depositTotal)}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span>Shipping ({getShippingLabel()})</span>
            <span>{getShippingCost() === 0 ? 'FREE' : formatCurrency(getShippingCost())}</span>
          </div>
          <div className="flex justify-between">
            <span>VAT (20%)</span>
            <span>{formatCurrency(effectiveVAT())}</span>
          </div>
          <div className="flex justify-between font-bold text-lg border-t pt-2">
            <span>{usingUpfront ? 'Total to pay today' : 'Total'}</span>
            <span>{formatCurrency(effectiveTotal())}</span>
          </div>
        </div>
      </div>

      <div className="pt-4">
        <label className="flex items-start gap-2">
          <input
            type="checkbox"
            checked={formData.terms_accepted}
            onChange={(e) => setFormData({ ...formData, terms_accepted: e.target.checked })}
            className="mt-1"
          />
          <span className="text-sm">
            I agree to the <a href="/terms" className="text-silux-primary hover:underline">Terms and Conditions</a> and{' '}
            <a href="/privacy" className="text-silux-primary hover:underline">Privacy Policy</a>
          </span>
        </label>
      </div>

      {createOrderMutation.error && (
        <Alert variant="error">Failed to create order. Please try again.</Alert>
      )}
      {payError && (
        <Alert variant="error">{payError}</Alert>
      )}

      <div className="flex justify-between pt-4">
        <Button
          variant="outline"
          onClick={() => setCurrentStep(getShippingStepNumber())}
        >
          Back
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmitOrder}
          disabled={!formData.terms_accepted || createOrderMutation.isPending}
        >
          {createOrderMutation.isPending ? 'Processing...' : 'Place Order'}
        </Button>
      </div>
    </div>
  )

  // Payment step — show the Stripe PaymentElement (card + Apple Pay + Google Pay) once a
  // PaymentIntent client secret is ready. The order is created but stays unpaid until this
  // succeeds.
  if (payState.step === 'paying' && payState.clientSecret) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <h1 className="text-2xl xs:text-3xl font-bold mb-2">Complete Payment</h1>
        <p className="text-gray-500 mb-6">Your order is reserved — enter your payment details to confirm it.</p>
        {payError && (
          <Alert variant="error" className="mb-4">{payError}</Alert>
        )}
        <StripePayment
          clientSecret={payState.clientSecret}
          amount={payState.total ?? 0}
          onSuccess={handlePaymentSuccess}
          onError={(msg) => setPayError(msg)}
        />
        <button
          type="button"
          onClick={() => setPayState({ step: 'idle' })}
          className="mt-4 text-sm text-gray-500 hover:text-gray-700 underline"
        >
          Cancel and return to checkout
        </button>
      </div>
    )
  }

  // Not signed in? Don't march them through the whole form only to fail at "Place Order" with a
  // raw "Not authenticated" (an account is required — contracts/Direct Debit need one). Gate it
  // up-front with a friendly choice; the basket is preserved and they return here after signing
  // in (login carries ?returnTo=/checkout). If a session expires mid-checkout the auth store
  // clears `user`, so this same gate reappears automatically instead of a scary error.
  if (!user && salesCfg?.checkout_inline_signup !== false) {
    return <CheckoutAccount />
  }
  if (!user) {
    // Stash where to come back to. sessionStorage (not a URL param) survives the OTP step and
    // isn't affected by mid-navigation URL changes — the auth route guard reads this to return
    // the customer here after signing in, instead of the dashboard.
    const goAuth = (path: string) => {
      setAuthReturn('/checkout')
      navigate(path)
    }
    return (
      <div className="max-w-md mx-auto px-4 py-16 text-center">
        <h1 className="text-2xl font-bold mb-2">Almost there</h1>
        <p className="text-gray-600 mb-8">
          To complete your order you'll need to sign in or create an account — your basket is saved.
        </p>
        <div className="space-y-3">
          <Button variant="primary" className="w-full" onClick={() => goAuth('/login')}>
            Sign in
          </Button>
          <Button variant="outline" className="w-full" onClick={() => goAuth('/register')}>
            Create an account
          </Button>
        </div>
        <p className="text-sm text-gray-500 mt-8">
          Already a customer but can't sign in?{' '}
          <button type="button" onClick={() => navigate('/forgot-password')} className="text-silux-primary underline">
            Send me a setup link
          </button>
        </p>
        <button type="button" onClick={() => navigate('/cart')} className="mt-6 block mx-auto text-sm text-gray-400 underline">
          Back to basket
        </button>
      </div>
    )
  }

  return (
    <div className="max-w-4xl lg:max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <h1 className="text-2xl xs:text-3xl font-bold mb-6 sm:mb-8">Checkout</h1>

      {/* Progress Steps */}
      <div className="flex items-center justify-center mb-6 sm:mb-8 overflow-x-auto px-4">
        {Array.from({ length: totalSteps }, (_, i) => i + 1).map((step) => (
          <div key={step} className="flex items-center">
            <div
              className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-sm sm:text-base font-semibold flex-shrink-0 ${
                currentStep >= step ? 'bg-silux-primary text-white' : 'bg-gray-200 text-gray-600'
              }`}
              title={getStepLabel(step)}
            >
              {step}
            </div>
            {step < totalSteps && (
              <div className={`w-12 sm:w-24 h-1 flex-shrink-0 ${currentStep > step ? 'bg-silux-primary' : 'bg-gray-200'}`} />
            )}
          </div>
        ))}
      </div>

      {/* Step Labels */}
      <div className="flex items-center justify-center mb-4 text-xs text-gray-500 gap-4 sm:gap-8 flex-wrap">
        {Array.from({ length: totalSteps }, (_, i) => i + 1).map((step) => (
          <span
            key={step}
            className={currentStep === step ? 'font-semibold text-silux-primary' : ''}
          >
            {getStepLabel(step)}
          </span>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
        <div className="lg:col-span-2 order-2 lg:order-1">
          <Card className="p-4 sm:p-6">
            {currentStep === 1 && renderStep1()}
            {hasEligibleSimItems && currentStep === getSimFormatStepNumber() && renderSimFormatStep()}
            {currentStep === getShippingStepNumber() && renderStep2()}
            {currentStep === getReviewStepNumber() && renderStep3()}
          </Card>
        </div>

        <div className="order-1 lg:order-2">
          <Card className="p-4 sm:p-6 lg:sticky lg:top-4">
            <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Order Summary</h3>
            <div className="space-y-2">
              {items.map((item) => (
                <div key={item.product.id} className="flex justify-between text-xs sm:text-sm gap-2">
                  <span className="flex-1">{item.product.name} x {item.quantity}</span>
                  <span className="font-medium flex-shrink-0">{formatCurrency((item.product.monthly_price || item.product.one_time_price || 0) * item.quantity)}</span>
                </div>
              ))}
            </div>
            <div className="border-t mt-4 pt-4">
              <div className="flex justify-between">
                <span>{usingUpfront ? 'Upfront (full term)' : 'Subtotal'}</span>
                <span>{formatCurrency(effectiveSubtotal())}</span>
              </div>
              {usingUpfront && upfrontQuote && upfrontQuote.discount_percent > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Upfront discount ({upfrontQuote.discount_percent}%)</span>
                  <span>-{formatCurrency(upfrontQuote.savings)}</span>
                </div>
              )}
              {!usingUpfront && discountApplied && (
                <div className="flex justify-between text-green-600">
                  <span>Discount</span>
                  <span>-{formatCurrency(calculateDiscount())}</span>
                </div>
              )}
              {depositTotal > 0 && (
                <div className="flex justify-between text-sm">
                  <span>Refundable holding deposit</span>
                  <span>{formatCurrency(depositTotal)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span>Shipping</span>
                <span>{getShippingCost() === 0 ? 'FREE' : formatCurrency(getShippingCost())}</span>
              </div>
              <div className="flex justify-between">
                <span>VAT (20%)</span>
                <span>{formatCurrency(effectiveVAT())}</span>
              </div>
              <div className="flex justify-between font-bold text-lg mt-2 pt-2 border-t">
                <span>{usingUpfront ? 'Total today' : 'Total'}</span>
                <span>{formatCurrency(effectiveTotal())}</span>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Spin & Save ineligible product popup */}
      {spinWheelBlockedProducts && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center"
            style={{ animation: 'swPop .35s cubic-bezier(.34,1.56,.64,1)' }}>
            <style>{`@keyframes swPop{from{transform:scale(.6);opacity:0}to{transform:scale(1);opacity:1}}`}</style>
            <div className="text-5xl mb-4">🎡</div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Spin &amp; Save Discount Not Applicable</h3>
            <p className="text-gray-600 mb-4">
              Your Spin &amp; Save discount code cannot be applied to the following product{spinWheelBlockedProducts.length > 1 ? 's' : ''}:
            </p>
            <ul className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4 text-left">
              {spinWheelBlockedProducts.map((name, i) => (
                <li key={i} className="text-red-700 font-semibold text-sm py-0.5">✕ {name}</li>
              ))}
            </ul>
            <p className="text-gray-500 text-sm mb-6">
              This product is not part of the Spin &amp; Save promotion. The discount is valid for new eligible product orders only.
              Please remove the ineligible product or choose a different order to use your code.
            </p>
            <button
              onClick={() => setSpinWheelBlockedProducts(null)}
              className="w-full py-3 bg-primary-600 hover:bg-primary-700 text-white font-bold rounded-xl transition-colors"
            >
              OK, I understand
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
